import { useState } from 'react'
import { API, graphqlOperation } from 'aws-amplify'
import { HomeIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'

import { mutation } from '../api'
import Button from '../components/Button'
import { useNotificationContext } from '../context/notification-context'
import { getCareer, spacesByCareerId } from '../api/queries'

const pages = [
  { name: 'System', to: '/system', current: false },
  { name: 'Assessments verschieben', to: '#', current: true }
]

const validationSchema = Yup.object().shape({
  source_career_id: Yup.string().required('Quell-Stellen ID ist erforderlich'),
  target_career_id: Yup.string().required('Ziel-Stellen ID ist erforderlich')
})

// Extract a readable message from an Amplify / GraphQL error object.
const extractErrorMessage = (err) => {
  if (!err) return 'Unbekannter Fehler'
  // Amplify wraps GraphQL errors in err.errors[]
  if (err.errors && err.errors.length > 0) {
    return err.errors
      .map(
        (e) =>
          `[${e.errorType || e.extensions?.code || 'GraphQL'}] ${e.message}`
      )
      .join(' | ')
  }
  return err.message || JSON.stringify(err)
}

// Replace all occurrences of oldId with newId in an authorization array.
// Items that don't contain oldId are left untouched.
const updateAuthFields = (authArray, oldId, newId) => {
  if (!authArray || !Array.isArray(authArray)) return []
  return authArray.map((item) =>
    typeof item === 'string' && item.includes(oldId)
      ? item.replace(oldId, newId)
      : item
  )
}

const MoveSpaces = () => {
  const { success, error } = useNotificationContext()

  const [sourceCareer, setSourceCareer] = useState(null)
  const [targetCareer, setTargetCareer] = useState(null)
  const [spaces, setSpaces] = useState([])
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [moveComplete, setMoveComplete] = useState(false)
  const [moveStats, setMoveStats] = useState({
    movedSpaces: 0,
    updatedPersons: 0,
    errors: []
  })
  const [backupData, setBackupData] = useState(null)

  // ─── Fetch helpers ────────────────────────────────────────────────────────

  const fetchCareer = async (career_id) => {
    try {
      const res = await API.graphql(
        graphqlOperation(getCareer, { id: career_id })
      )
      return res.data.getCareer || null
    } catch (err) {
      error(`Fehler beim Laden der Stelle: ${extractErrorMessage(err)}`)
      return null
    }
  }

  const fetchSourceCareer = async (career_id) => {
    setSourceCareer(null)
    setSpaces([])
    setMoveComplete(false)
    setBackupData(null)

    const career = await fetchCareer(career_id)
    if (!career) return

    setSourceCareer(career)
    await fetchSpaces(career_id)
  }

  const fetchTargetCareer = async (career_id) => {
    setTargetCareer(null)
    setMoveComplete(false)
    setBackupData(null)

    const career = await fetchCareer(career_id)
    if (!career) return

    setTargetCareer(career)
  }

  const fetchSpaces = async (career_id) => {
    setIsLoadingSpaces(true)
    try {
      // Paginate through all spaces for this career
      let allSpaces = []
      let nextToken = null

      do {
        const result = await API.graphql(
          graphqlOperation(spacesByCareerId, {
            career_id,
            limit: 1000,
            nextToken
          })
        )
        const page = result.data.spacesByCareerId
        allSpaces = allSpaces.concat(page.items || [])
        nextToken = page.nextToken
      } while (nextToken)

      setSpaces(allSpaces)
    } catch (err) {
      error(`Fehler beim Laden der Assessments: ${extractErrorMessage(err)}`)
      setSpaces([])
    } finally {
      setIsLoadingSpaces(false)
    }
  }

  // ─── Backup ───────────────────────────────────────────────────────────────

  const generateBackup = (spacesToMove, src, tgt) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const data = {
      timestamp,
      sourceCareer: { id: src.id, partner_id: src.partner_id, name: src.name },
      targetCareer: { id: tgt.id, partner_id: tgt.partner_id, name: tgt.name },
      spaces: spacesToMove.map((s) => ({
        id: s.id,
        authorized: s.authorized,
        partner_id: s.partner_id,
        career_id: s.career_id,
        created_by_partner_id: s.created_by_partner_id,
        authorized_create: s.authorized_create,
        authorized_read: s.authorized_read,
        authorized_update: s.authorized_update,
        authorized_delete: s.authorized_delete
      })),
      persons: spacesToMove
        .filter((s) => s.person)
        .map((s) => ({
          id: s.person.id,
          authorized: s.person.authorized,
          partner_id: s.person.partner_id,
          authorized_create: s.person.authorized_create,
          authorized_read: s.person.authorized_read,
          authorized_update: s.person.authorized_update,
          authorized_delete: s.person.authorized_delete
        }))
    }
    return {
      data,
      filename: `move-spaces-backup-${src.id}-to-${tgt.id}-${timestamp}.json`
    }
  }

  const downloadBackup = (backup) => {
    const dataStr = JSON.stringify(backup.data, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = backup.filename
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 100)
  }

  // ─── Move logic ───────────────────────────────────────────────────────────

  const moveSpaces = async (values) => {
    if (!sourceCareer || !targetCareer || spaces.length === 0) return

    const oldPartnerId = sourceCareer.partner_id
    const newPartnerId = targetCareer.partner_id
    const newCareerId = targetCareer.id
    const isCrossPartner = oldPartnerId !== newPartnerId

    // 1. Generate and download backup first
    const backup = generateBackup(spaces, sourceCareer, targetCareer)
    setBackupData(backup)
    downloadBackup(backup)

    setIsMoving(true)
    setMoveComplete(false)

    let movedSpaces = 0
    const updatedPersonIds = new Set()
    const errors = []

    for (const space of spaces) {
      try {
        // ── Space update input ──────────────────────────────────────────────
        const spaceInput = { id: space.id }

        spaceInput.career_id = newCareerId

        if (isCrossPartner) {
          spaceInput.partner_id = newPartnerId

          // ByContextByPartnerIDByDate is a composite GSI (context + partner_id + createdAt).
          // AppSync requires ALL fields of a composite sort key when any one of them is updated.
          spaceInput.context = space.context
          spaceInput.createdAt = space.createdAt

          if (space.created_by_partner_id) {
            spaceInput.created_by_partner_id = newPartnerId
          }

          // authorized (legacy array — replace old partner_id occurrences)
          if (space.authorized && space.authorized.length > 0) {
            spaceInput.authorized = updateAuthFields(
              space.authorized,
              oldPartnerId,
              newPartnerId
            )
          }

          spaceInput.authorized_create = updateAuthFields(
            space.authorized_create,
            oldPartnerId,
            newPartnerId
          )
          spaceInput.authorized_read = updateAuthFields(
            space.authorized_read,
            oldPartnerId,
            newPartnerId
          )
          spaceInput.authorized_update = updateAuthFields(
            space.authorized_update,
            oldPartnerId,
            newPartnerId
          )
          spaceInput.authorized_delete = updateAuthFields(
            space.authorized_delete,
            oldPartnerId,
            newPartnerId
          )
        }

        await mutation({ mutation: 'updateSpace', input: spaceInput })
        movedSpaces++

        // ── Person update (if present and not already handled) ──────────────
        if (space.person && !updatedPersonIds.has(space.person.id)) {
          const person = space.person
          const personInput = { id: person.id }

          if (isCrossPartner) {
            personInput.partner_id = newPartnerId

            if (person.authorized && person.authorized.length > 0) {
              personInput.authorized = updateAuthFields(
                person.authorized,
                oldPartnerId,
                newPartnerId
              )
            }

            personInput.authorized_create = updateAuthFields(
              person.authorized_create,
              oldPartnerId,
              newPartnerId
            )
            personInput.authorized_read = updateAuthFields(
              person.authorized_read,
              oldPartnerId,
              newPartnerId
            )
            personInput.authorized_update = updateAuthFields(
              person.authorized_update,
              oldPartnerId,
              newPartnerId
            )
            personInput.authorized_delete = updateAuthFields(
              person.authorized_delete,
              oldPartnerId,
              newPartnerId
            )
          }

          await mutation({ mutation: 'updatePerson', input: personInput })
          updatedPersonIds.add(person.id)
        }
      } catch (err) {
        errors.push({
          spaceId: space.id,
          message: extractErrorMessage(err)
        })
      }
    }

    setMoveStats({
      movedSpaces,
      updatedPersons: updatedPersonIds.size,
      errors
    })
    setMoveComplete(true)
    setIsMoving(false)

    if (errors.length === 0) {
      success(`${movedSpaces} Assessments wurden erfolgreich verschoben.`)
    } else {
      error(
        `${movedSpaces} Assessments verschoben, aber ${errors.length} Fehler aufgetreten.`
      )
    }
  }

  // ─── Derived state ────────────────────────────────────────────────────────

  const isSameCareer =
    sourceCareer && targetCareer && sourceCareer.id === targetCareer.id

  const canSubmit =
    sourceCareer &&
    targetCareer &&
    spaces.length > 0 &&
    !isSameCareer &&
    !isMoving

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Breadcrumb */}
      <nav className='flex mb-8' aria-label='Breadcrumb'>
        <ol className='flex items-center space-x-4'>
          <li>
            <div>
              <Link to='/' className='text-gray-400 hover:text-gray-500'>
                <HomeIcon
                  className='h-5 w-5 flex-shrink-0'
                  aria-hidden='true'
                />
                <span className='sr-only'>Home</span>
              </Link>
            </div>
          </li>
          {pages.map((page) => (
            <li key={page.name}>
              <div className='flex items-center'>
                <svg
                  className='h-5 w-5 flex-shrink-0 text-gray-300'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  aria-hidden='true'
                >
                  <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
                </svg>
                <Link
                  to={page.to}
                  className={`ml-4 text-sm font-medium ${
                    page.current
                      ? 'text-indigo-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  aria-current={page.current ? 'page' : undefined}
                >
                  {page.name}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      {/* Header */}
      <header className='mb-8'>
        <h1 className='text-3xl font-bold leading-tight tracking-tight text-gray-900'>
          Assessments verschieben
        </h1>
        <p className='mt-2 text-sm text-gray-700'>
          Verschiebt alle Assessments (Spaces) und die dazugehörigen Personen
          von einer Stelle zu einer anderen. Die Felder{' '}
          <code className='text-xs bg-gray-100 px-1 rounded'>partner_id</code>,{' '}
          <code className='text-xs bg-gray-100 px-1 rounded'>career_id</code>{' '}
          sowie alle Autorisierungs-IDs werden direkt in den bestehenden
          Datensätzen aktualisiert. Vor dem Verschieben wird automatisch ein
          Backup heruntergeladen.
        </p>
      </header>

      <div className='bg-white shadow sm:rounded-lg'>
        <div className='px-4 py-5 sm:p-6'>
          <Formik
            initialValues={{ source_career_id: '', target_career_id: '' }}
            validationSchema={validationSchema}
            onSubmit={moveSpaces}
          >
            {({ errors, touched, values, isSubmitting }) => (
              <Form className='space-y-6'>
                {/* ── Source career ── */}
                <div>
                  <label
                    htmlFor='source_career_id'
                    className='block text-sm font-medium leading-6 text-gray-900'
                  >
                    Quell-Stellen ID
                  </label>
                  <div className='mt-2 flex'>
                    <Field
                      type='text'
                      name='source_career_id'
                      id='source_career_id'
                      className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3'
                      placeholder='career_id der Quell-Stelle'
                    />
                    <Button.PrimaryLG
                      text='Laden'
                      className='ml-4'
                      onClick={() => fetchSourceCareer(values.source_career_id)}
                      disabled={!values.source_career_id}
                    />
                  </div>
                  {errors.source_career_id && touched.source_career_id && (
                    <div className='text-sm text-red-600 mt-1'>
                      {errors.source_career_id}
                    </div>
                  )}
                </div>

                {/* ── Source career details + spaces ── */}
                {sourceCareer && (
                  <div className='bg-gray-50 p-4 rounded-md'>
                    <h3 className='text-base font-semibold leading-6 text-gray-900'>
                      Quell-Stelle
                    </h3>
                    <dl className='mt-2 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2'>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {sourceCareer.id}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {sourceCareer.name}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Partner ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {sourceCareer.partner_id}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Partner Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {sourceCareer.partner_name}
                        </dd>
                      </div>
                    </dl>

                    {/* Spaces table */}
                    <div className='mt-6'>
                      <h4 className='text-sm font-semibold text-gray-900'>
                        Assessments ({spaces.length})
                      </h4>
                      {isLoadingSpaces ? (
                        <div className='mt-2 flex justify-center'>
                          <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500' />
                        </div>
                      ) : spaces.length > 0 ? (
                        <div className='mt-2 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg'>
                          <table className='min-w-full divide-y divide-gray-300'>
                            <thead className='bg-gray-50'>
                              <tr>
                                <th
                                  scope='col'
                                  className='py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500 sm:pl-6'
                                >
                                  Person
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Status
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Sprache
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Erstellt
                                </th>
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-200 bg-white'>
                              {spaces.map((space) => (
                                <tr key={space.id}>
                                  <td className='whitespace-nowrap py-2 pl-4 pr-3 text-xs text-gray-900 sm:pl-6'>
                                    {space.person
                                      ? `${space.person.firstname || ''} ${space.person.lastname || ''}`.trim() ||
                                        space.person.email ||
                                        'Unbekannt'
                                      : 'Unbekannt'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {space.application_status || 'N/A'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {space.language || 'N/A'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {space.createdAt
                                      ? new Date(
                                          space.createdAt
                                        ).toLocaleDateString()
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className='mt-2 text-sm text-gray-500'>
                          Keine Assessments gefunden.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Target career ── */}
                <div>
                  <label
                    htmlFor='target_career_id'
                    className='block text-sm font-medium leading-6 text-gray-900'
                  >
                    Ziel-Stellen ID
                  </label>
                  <div className='mt-2 flex'>
                    <Field
                      type='text'
                      name='target_career_id'
                      id='target_career_id'
                      className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3'
                      placeholder='career_id der Ziel-Stelle'
                    />
                    <Button.PrimaryLG
                      text='Laden'
                      className='ml-4'
                      onClick={() => fetchTargetCareer(values.target_career_id)}
                      disabled={!values.target_career_id}
                    />
                  </div>
                  {errors.target_career_id && touched.target_career_id && (
                    <div className='text-sm text-red-600 mt-1'>
                      {errors.target_career_id}
                    </div>
                  )}
                  {isSameCareer && (
                    <div className='text-sm text-red-600 mt-1'>
                      Quell- und Ziel-Stelle sind identisch.
                    </div>
                  )}
                </div>

                {/* ── Target career details ── */}
                {targetCareer && (
                  <div className='bg-gray-50 p-4 rounded-md'>
                    <h3 className='text-base font-semibold leading-6 text-gray-900'>
                      Ziel-Stelle
                    </h3>
                    <dl className='mt-2 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2'>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {targetCareer.id}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {targetCareer.name}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Partner ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {targetCareer.partner_id}
                          {sourceCareer &&
                            targetCareer.partner_id !==
                              sourceCareer.partner_id && (
                              <span className='ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800'>
                                anderer Partner
                              </span>
                            )}
                          {sourceCareer &&
                            targetCareer.partner_id ===
                              sourceCareer.partner_id && (
                              <span className='ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'>
                                gleicher Partner
                              </span>
                            )}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Partner Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {targetCareer.partner_name}
                        </dd>
                      </div>
                    </dl>

                    {/* Warning for cross-partner move */}
                    {sourceCareer &&
                      targetCareer.partner_id !== sourceCareer.partner_id && (
                        <div className='mt-4 rounded-md bg-yellow-50 p-3'>
                          <p className='text-sm text-yellow-800'>
                            <strong>Cross-Partner Verschiebung:</strong> Die
                            Spaces und Personen werden auf Partner{' '}
                            <strong>{targetCareer.partner_id}</strong>{' '}
                            umgeschrieben. Alle Autorisierungs-IDs werden
                            entsprechend aktualisiert.
                          </p>
                        </div>
                      )}
                  </div>
                )}

                {/* ── Submit ── */}
                <div className='flex justify-end'>
                  <Button.PrimaryXL
                    text={isMoving ? 'Verschiebe...' : 'Verschieben'}
                    type='submit'
                    isLoading={isMoving}
                    disabled={!canSubmit || isSubmitting}
                  />
                </div>
              </Form>
            )}
          </Formik>

          {/* ── Result ── */}
          {moveComplete && (
            <div className='mt-6 bg-green-50 p-4 rounded-md'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-green-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-green-800'>
                    Verschieben abgeschlossen
                  </h3>
                  <div className='mt-2 text-sm text-green-700'>
                    <ul className='list-disc space-y-1 pl-5'>
                      <li>{moveStats.movedSpaces} Assessments verschoben</li>
                      <li>{moveStats.updatedPersons} Personen aktualisiert</li>
                    </ul>

                    {moveStats.errors.length > 0 && (
                      <div className='mt-3'>
                        <p className='font-medium text-red-700'>
                          {moveStats.errors.length} Fehler:
                        </p>
                        <ul className='space-y-2 pl-2 mt-1'>
                          {moveStats.errors.map((e, i) => (
                            <li
                              key={i}
                              className='text-xs text-red-700 bg-red-50 rounded p-2 font-mono break-all'
                            >
                              <span className='font-semibold'>
                                Space {e.spaceId}:
                              </span>{' '}
                              {e.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {backupData && (
                      <div className='mt-4 flex'>
                        <button
                          type='button'
                          onClick={() => downloadBackup(backupData)}
                          className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5 mr-2'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                            />
                          </svg>
                          Backup nochmals herunterladen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MoveSpaces
